import {
  hasAtLeastOneCommunicationMethod,
  hasAtLeastOneSocialLink,
  normalizeGigProfileFormValues,
} from '../../utils/gigProfileFields';

export function normalizeGigSubmissionResponse(payload = {}) {
  return {
    gig: {
      id: Number(payload?.gig?.id || 0) || null,
      siteId: Number(payload?.gig?.siteId || 0) || null,
      siteName: String(payload?.gig?.siteName || '').trim(),
      websiteSlug: String(payload?.gig?.websiteSlug || '').trim(),
      title: String(payload?.gig?.title || '').trim(),
      slug: String(payload?.gig?.slug || '').trim(),
      description: String(payload?.gig?.description || '').trim(),
      startsAtUtc: payload?.gig?.startsAtUtc || null,
      endsAtUtc: payload?.gig?.endsAtUtc || null,
      timezoneName: payload?.gig?.timezoneName || null,
      utcOffsetMinutesSnapshot:
        payload?.gig?.utcOffsetMinutesSnapshot == null ? null : Number(payload.gig.utcOffsetMinutesSnapshot),
      status: String(payload?.gig?.status || 'draft').toLowerCase(),
      publishedAt: payload?.gig?.publishedAt || null,
    },
    performer: {
      id: Number(payload?.performer?.id || 0) || null,
      displayName: String(payload?.performer?.displayName || '').trim(),
      instagramUrl: String(payload?.performer?.instagramUrl || '').trim(),
      facebookUrl: String(payload?.performer?.facebookUrl || '').trim(),
      twitterUrl: String(payload?.performer?.twitterUrl || '').trim(),
      pinterestUrl: String(payload?.performer?.pinterestUrl || '').trim(),
      appleMusicUrl: String(payload?.performer?.appleMusicUrl || '').trim(),
      mixcloudUrl: String(payload?.performer?.mixcloudUrl || '').trim(),
      tiktokUrl: String(payload?.performer?.tiktokUrl || '').trim(),
      bio: String(payload?.performer?.bio || '').trim(),
      genres: Array.isArray(payload?.performer?.genres) ? payload.performer.genres.filter(Boolean) : [],
      soundcloudUrl: String(payload?.performer?.soundcloudUrl || '').trim(),
      spotifyUrl: String(payload?.performer?.spotifyUrl || '').trim(),
      youtubeUrl: String(payload?.performer?.youtubeUrl || '').trim(),
      contactEmail: String(payload?.performer?.contactEmail || '').trim(),
      contactPhone: String(payload?.performer?.contactPhone || '').trim(),
      preferredContactMethod: String(payload?.performer?.preferredContactMethod || '').trim(),
      preferredContactDetails: String(payload?.performer?.preferredContactDetails || '').trim(),
      storeProfile: payload?.performer?.storeProfile !== false,
      allowContactSharing: !!payload?.performer?.allowContactSharing,
      artworkCreativeId:
        payload?.performer?.artworkCreativeId == null ? null : Number(payload.performer.artworkCreativeId),
      artworkPreviewUrl: payload?.performer?.artworkPreviewUrl || null,
      artworkPreviewUrls: Array.isArray(payload?.performer?.artworkPreviewUrls)
        ? payload.performer.artworkPreviewUrls.filter(Boolean)
        : payload?.performer?.artworkPreviewUrl
          ? [payload.performer.artworkPreviewUrl]
          : [],
      confirmed: !!payload?.performer?.confirmed,
      status: String(payload?.performer?.status || 'awaiting_submission').toLowerCase(),
      submittedAt: payload?.performer?.submittedAt || null,
      inviteSentAt: payload?.performer?.inviteSentAt || null,
      inviteChannel: payload?.performer?.inviteChannel || null,
      inviteTarget: payload?.performer?.inviteTarget || null,
    },
    submission: {
      token: payload?.submission?.token || null,
      canSubmit: payload?.submission?.canSubmit !== false,
      alreadySubmitted: !!payload?.submission?.alreadySubmitted,
    },
  };
}

export function createGigSubmissionForm(submission) {
  const normalized = normalizeGigSubmissionResponse(submission);
  return {
    displayName: normalized.performer.displayName,
    instagramUrl: normalized.performer.instagramUrl,
    facebookUrl: normalized.performer.facebookUrl,
    twitterUrl: normalized.performer.twitterUrl,
    pinterestUrl: normalized.performer.pinterestUrl,
    appleMusicUrl: normalized.performer.appleMusicUrl,
    mixcloudUrl: normalized.performer.mixcloudUrl,
    tiktokUrl: normalized.performer.tiktokUrl,
    bio: normalized.performer.bio,
    genresText: normalized.performer.genres.join(', '),
    soundcloudUrl: normalized.performer.soundcloudUrl,
    spotifyUrl: normalized.performer.spotifyUrl,
    youtubeUrl: normalized.performer.youtubeUrl,
    contactEmail: normalized.performer.contactEmail,
    contactPhone: normalized.performer.contactPhone,
    preferredContactMethod: normalized.performer.preferredContactMethod,
    preferredContactDetails: normalized.performer.preferredContactDetails,
    storeProfile: normalized.performer.storeProfile,
    allowContactSharing: normalized.performer.allowContactSharing,
    artworkCreativeId: normalized.performer.artworkCreativeId,
    artworkPreviewUrl: normalized.performer.artworkPreviewUrl,
    artworkPreviewUrls: normalized.performer.artworkPreviewUrls,
    confirmed: normalized.performer.confirmed,
  };
}

export function validateGigSubmissionForm(form) {
  const normalized = normalizeGigProfileFormValues(form);
  if (!String(form?.displayName || '').trim()) {
    return 'Artist name is required';
  }
  if (!String(form?.bio || '').trim()) {
    return 'Short bio is required';
  }
  const genres = String(form?.genresText || '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
  if (!genres.length) {
    return 'At least one genre is required';
  }
  if (!hasAtLeastOneSocialLink(normalized)) {
    return 'Add at least one social or music link';
  }
  if (!hasAtLeastOneCommunicationMethod(normalized)) {
    return 'Add at least one way for the venue to contact you';
  }
  if (!form?.artworkCreativeId) {
    return 'Artwork upload is required';
  }
  if (!form?.confirmed) {
    return 'You must confirm attendance before submitting';
  }
  return null;
}

export function buildGigSubmissionRequestBody(form) {
  const normalized = normalizeGigProfileFormValues(form);
  return {
    displayName: String(form?.displayName || '').trim(),
    instagramUrl: normalized.instagramUrl,
    facebookUrl: normalized.facebookUrl,
    twitterUrl: normalized.twitterUrl,
    pinterestUrl: normalized.pinterestUrl,
    appleMusicUrl: normalized.appleMusicUrl,
    mixcloudUrl: normalized.mixcloudUrl,
    tiktokUrl: normalized.tiktokUrl,
    bio: String(form?.bio || '').trim(),
    genres: String(form?.genresText || '')
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean),
    soundcloudUrl: normalized.soundcloudUrl,
    spotifyUrl: normalized.spotifyUrl,
    youtubeUrl: normalized.youtubeUrl,
    contactEmail: normalized.contactEmail,
    contactPhone: normalized.contactPhone,
    preferredContactMethod: normalized.preferredContactMethod,
    preferredContactDetails: normalized.preferredContactDetails,
    storeProfile: normalized.storeProfile,
    allowContactSharing: normalized.allowContactSharing,
    artworkCreativeId: Number(form?.artworkCreativeId),
    confirmed: !!form?.confirmed,
  };
}
